// Maps GET /api/market/ipos/upcoming items -- NSE's forthcoming-issue rows
// ({ companyName, symbol, series, issueStartDate, issueEndDate, issuePrice,
// issueSize, lotSize?, status }) -- into the cards the Upcoming IPOs page's
// "Opening Soon" section renders. lotSize is only present for some issues
// (NSE lists it for SME rows), so everything derived from it is optional.
import { deriveBoard, parsePriceRange, toIsoDate } from './ipoCatalogAdapters';
import { toGmpSlug } from './gmpDataMapper';
import { formatDayShort, todayIso } from './ipoCalendar';
import { daysBetween } from './upcomingIpos';

const toNumber = (value) => {
  const number = Number(value);
  return value !== null && value !== '' && Number.isFinite(number) && number > 0 ? number : null;
};

const phaseFor = (openOn, closeOn, today) => {
  if (!openOn || today < openOn) return 'upcoming';
  if (closeOn && today > closeOn) return 'closed';
  return 'open';
};

/**
 * @param {object} item - one entry of GET /api/market/ipos/upcoming -> data.items
 * @param {string} today - local ISO date ("2026-09-21"), injectable for tests
 */
export const mapOpeningSoonIpo = (item, today = todayIso()) => {
  const company = String(item?.companyName || item?.symbol || '').trim();
  if (!company) return null;

  const openOn = toIsoDate(item.issueStartDate);
  const closeOn = toIsoDate(item.issueEndDate);
  const { low, high } = parsePriceRange(item.issuePrice || item.priceBand);
  const shares = toNumber(item.issueSize);
  const lotSize = toNumber(item.lotSize);
  const board = deriveBoard(item.series);

  return {
    id: item.symbol || company,
    symbol: item.symbol || '',
    company,
    board,
    boardLabel: board === 'sme' ? 'SME' : 'Mainboard',
    openOn,
    closeOn,
    phase: phaseFor(openOn, closeOn, today),
    daysToOpen: openOn ? daysBetween(today, openOn) : null,
    priceBand: high ? (low && low !== high ? `₹${low} – ₹${high}` : `₹${high}`) : null,
    lotSize,
    minInvestment: lotSize && high ? lotSize * high : null,
    shares,
    // Issue size at the upper end of the price band
    sizeCr: shares && high ? Math.round(((shares * high) / 1e7) * 100) / 100 : null,
    gmpSlug: toGmpSlug(company),
  };
};

// Soonest first; issues whose bidding already closed are dropped
export const mapOpeningSoonIpos = (items, today = todayIso()) =>
  (items || [])
    .map((item) => mapOpeningSoonIpo(item, today))
    .filter((row) => row && row.phase !== 'closed')
    .sort(
      (a, b) =>
        (a.openOn || '9999').localeCompare(b.openOn || '9999') ||
        (a.closeOn || '').localeCompare(b.closeOn || '') ||
        a.company.localeCompare(b.company)
    );

export const OPENING_STATUS = {
  upcoming: { label: 'Upcoming', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  open: { label: 'Bidding Open', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export const filterOpeningSoon = (rows, { board = 'all', status = 'all' } = {}) =>
  rows.filter((row) => (board === 'all' || row.board === board) && (status === 'all' || row.phase === status));

export const openingLabel = (row, today = todayIso()) => {
  if (row.phase === 'open') return row.closeOn === today ? 'Closes today' : `Bidding open · closes ${formatDayShort(row.closeOn)}`;
  if (row.daysToOpen === null) return 'Dates to be announced';
  if (row.daysToOpen <= 0) return 'Opens today';
  if (row.daysToOpen === 1) return 'Opens tomorrow';
  return `Opens in ${row.daysToOpen} days`;
};

export const formatInr = (value) => (value ? `₹${Math.round(value).toLocaleString('en-IN')}` : null);

export const formatCr = (value) => (value ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr` : null);
